/**
 * Admin - Vibe Tenant Cluster Provision / Disable
 *
 * Operator passthrough surface for the per-tenant data-plane lifecycle.
 * Two endpoints over the existing `ProvisionTenantClusterUseCase` /
 * `DisableTenantClusterUseCase` — provision opts an org into its own
 * dedicated cluster, disable opts it back out (→ DISABLED_RETAINED, the
 * teardown reaper destroys the stacks after the grace window).
 *
 * Operator-driven by design: Phase 1 provisioning is manual, not
 * self-serve (rollout Phase 1). `AdminPermissionGuard` enforces PBAC;
 * `AdminAuditInterceptor` captures operator identity + outcome.
 * Cross-org — the operator names the target `organizationId` in the body
 * (not from a JWT org scope), so this never lives on the customer-facing
 * vibe controllers.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import {
  AdminVibeTenantClusterRunBackupNowBodySchema,
  AdminVibeTenantClusterRunBackupNowOutcomeReadSchema,
  AdminVibeTenantClusterRunRestoreDrillNowBodySchema,
  AdminVibeTenantClusterRunRestoreDrillNowOutcomeReadSchema
} from "./zadmin-vibe-tenant-cluster-backup-drill";
import {
  AdminVibeTenantClusterSetBuildExecutorBodySchema,
  AdminVibeTenantClusterSetBuildExecutorOutcomeReadSchema,
  AdminVibeTenantClusterSetOriginLockBodySchema,
  AdminVibeTenantClusterSetOriginLockOutcomeReadSchema
} from "./zadmin-vibe-tenant-cluster-ingress-build";
import { AdminVibeTenantClusterListItemSchema } from "./zadmin-vibe-tenant-cluster-list-item";
import {
  AdminVibeTenantClusterCompleteTeardownBodySchema,
  AdminVibeTenantClusterCompleteTeardownOutcomeReadSchema,
  AdminVibeTenantClusterForceConvergeBodySchema,
  AdminVibeTenantClusterForceConvergeOutcomeReadSchema,
  AdminVibeTenantClusterSetReconcilePauseBodySchema,
  AdminVibeTenantClusterSetReconcilePauseOutcomeReadSchema
} from "./zadmin-vibe-tenant-cluster-operator-repair";
import {
  AdminVibeTenantClusterRequestOrgGitCredentialRetirementBodySchema,
  AdminVibeTenantClusterRequestOrgGitCredentialRetirementOutcomeReadSchema
} from "./zadmin-vibe-tenant-cluster-org-git-credential-retirement";
import {
  AdminVibeTenantClusterDisableBodySchema,
  AdminVibeTenantClusterDisableOutcomeReadSchema,
  AdminVibeTenantClusterProvisionBodySchema,
  AdminVibeTenantClusterProvisionOutcomeReadSchema
} from "./zadmin-vibe-tenant-cluster-provision-disable";
import {
  AdminVibeTenantClusterRequestServerRollBodySchema,
  AdminVibeTenantClusterRequestServerRollOutcomeReadSchema
} from "./zadmin-vibe-tenant-cluster-server-roll";
import { AdminVibeTenantClusterStatusHistoryEndpoint } from "./zadmin-vibe-tenant-cluster-status-history";
import {
  AdminVibeTenantClusterRequestVaultRaftOperatorGrantBodySchema,
  AdminVibeTenantClusterRequestVaultRaftOperatorGrantOutcomeReadSchema
} from "./zadmin-vibe-tenant-cluster-vault-raft-operator-grant";

// ============================================================
// Allowed provisioning regions — RGPD: EU only, always
// ============================================================

// Re-exported from the shared vibe domain so the operator surface and the
// org's own opt-in offer exactly the same regions. Kept exported here for
// the existing importers of this contract.
export {
  VIBE_ALLOWED_REGIONS,
  type VibeAllowedRegion,
  VibeAllowedRegionSchema
} from "../../../shared/domain/vibe/allowed-regions";

// ============================================================
// Contract
// ============================================================

export const ZAdminVibeTenantCluster = {
  List: {
    method: "GET" as const,
    path: "/api/admin/vibe/tenant-cluster",
    Response: ApiSuccessSchema(z.array(AdminVibeTenantClusterListItemSchema))
  },
  Provision: {
    method: "POST" as const,
    path: "/api/admin/vibe/tenant-cluster/provision",
    Body: AdminVibeTenantClusterProvisionBodySchema,
    Response: ApiSuccessSchema(AdminVibeTenantClusterProvisionOutcomeReadSchema)
  },
  Disable: {
    method: "POST" as const,
    path: "/api/admin/vibe/tenant-cluster/disable",
    Body: AdminVibeTenantClusterDisableBodySchema,
    Response: ApiSuccessSchema(AdminVibeTenantClusterDisableOutcomeReadSchema)
  },
  SetReconcilePause: {
    method: "POST" as const,
    path: "/api/admin/vibe/tenant-cluster/reconcile-pause",
    Body: AdminVibeTenantClusterSetReconcilePauseBodySchema,
    Response: ApiSuccessSchema(AdminVibeTenantClusterSetReconcilePauseOutcomeReadSchema)
  },
  ForceConverge: {
    method: "POST" as const,
    path: "/api/admin/vibe/tenant-cluster/force-converge",
    Body: AdminVibeTenantClusterForceConvergeBodySchema,
    Response: ApiSuccessSchema(AdminVibeTenantClusterForceConvergeOutcomeReadSchema)
  },
  RequestServerRoll: {
    method: "POST" as const,
    path: "/api/admin/vibe/tenant-cluster/request-server-roll",
    Body: AdminVibeTenantClusterRequestServerRollBodySchema,
    Response: ApiSuccessSchema(AdminVibeTenantClusterRequestServerRollOutcomeReadSchema)
  },
  SetOriginLock: {
    method: "POST" as const,
    path: "/api/admin/vibe/tenant-cluster/origin-lock",
    Body: AdminVibeTenantClusterSetOriginLockBodySchema,
    Response: ApiSuccessSchema(AdminVibeTenantClusterSetOriginLockOutcomeReadSchema)
  },
  SetBuildExecutor: {
    method: "POST" as const,
    path: "/api/admin/vibe/tenant-cluster/build-executor",
    Body: AdminVibeTenantClusterSetBuildExecutorBodySchema,
    Response: ApiSuccessSchema(AdminVibeTenantClusterSetBuildExecutorOutcomeReadSchema)
  },
  CompleteTeardown: {
    method: "POST" as const,
    path: "/api/admin/vibe/tenant-cluster/complete-teardown",
    Body: AdminVibeTenantClusterCompleteTeardownBodySchema,
    Response: ApiSuccessSchema(AdminVibeTenantClusterCompleteTeardownOutcomeReadSchema)
  },
  RunBackupNow: {
    method: "POST" as const,
    path: "/api/admin/vibe/tenant-cluster/run-backup-now",
    Body: AdminVibeTenantClusterRunBackupNowBodySchema,
    Response: ApiSuccessSchema(AdminVibeTenantClusterRunBackupNowOutcomeReadSchema)
  },
  RunRestoreDrillNow: {
    method: "POST" as const,
    path: "/api/admin/vibe/tenant-cluster/run-restore-drill-now",
    Body: AdminVibeTenantClusterRunRestoreDrillNowBodySchema,
    Response: ApiSuccessSchema(AdminVibeTenantClusterRunRestoreDrillNowOutcomeReadSchema)
  },
  RequestVaultRaftOperatorGrant: {
    method: "POST" as const,
    path: "/api/admin/vibe/tenant-cluster/vault-raft-operator-grant",
    Body: AdminVibeTenantClusterRequestVaultRaftOperatorGrantBodySchema,
    Response: ApiSuccessSchema(AdminVibeTenantClusterRequestVaultRaftOperatorGrantOutcomeReadSchema)
  },
  RequestOrgGitCredentialRetirement: {
    method: "POST" as const,
    path: "/api/admin/vibe/tenant-cluster/org-git-credential-retirement",
    Body: AdminVibeTenantClusterRequestOrgGitCredentialRetirementBodySchema,
    Response: ApiSuccessSchema(
      AdminVibeTenantClusterRequestOrgGitCredentialRetirementOutcomeReadSchema
    )
  },
  StatusHistory: AdminVibeTenantClusterStatusHistoryEndpoint
} as const;
